import httpService from './httpService';
import queryString from 'query-string';
import { saveAs } from '@progress/kendo-file-saver';
import getDateAndTime from '../../common/utility/getDateAndTime';
import { formatDate } from '@progress/kendo-intl';

import { IClassification } from './types/IClassification';

export const getClassifications = (): Promise<IClassification[]> => {
  let endpoint = httpService.getEndpointURL('/Documents/classifications');
  return httpService.getTyped<IClassification[]>(endpoint);
};

export const getFeedClassifications = (): Promise<IClassification[]> => {
  let endpoint = httpService.getEndpointURL('/Classifications/documentsAndBoards');
  return httpService.getTyped<IClassification[]>(endpoint);
};

// TODO: We don't use this service - What should we do about it?
export const getDoc = (id: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/Documents/${id}`);
  return httpService.get(endpoint);
};

export const getDocAssigment = (assignId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/Documents/assignments/${assignId}`);
  return httpService.get(endpoint);
};

export const downloadDoc = (doc): Promise<any> => {
  const config = {
    headers: {
      'Content-type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': '*',
    },
    responseType: 'arraybuffer',
  };

  let endpoint = httpService.getEndpointURL(`/Documents/download/${doc.documentId}`);

  return (
    httpService
      .get(endpoint, config)
      .then((response) => {
        const data = response.data;
        const contentType = response.headers['content-type'];
        const blob = new Blob([data], { type: contentType });
        const name = doc.name ? doc.name : doc.label;
        saveAs(blob, name);
      })
      .then(() => registerDownload(doc))
  );
};

export const registerDownload = (doc): Promise<any> => {
  const config = {
    headers: {
      'Content-type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': '*',
    },
    responseType: 'arraybuffer',
  };
  let endpoint = httpService.getEndpointURL(`/Documents/${doc.documentId}/register/download`);

  return httpService.post(endpoint, config);
};

export const filterDocuments = (
  documentName,
  funds,
  investors,
  classifications,
  read,
  fromDate,
  toDate,
  startResult,
  maxResults,
  sortField,
  sortDir,
  publishedChecked,
  effectiveChecked,
  asOfDate,
  period,
  includeCount,
): Promise<any> => {
  let parsedQuery = queryString.parse(location.search);
  
  if (documentName) {
    parsedQuery.name = documentName;
  }
  if (funds && funds.length > 0) {
    parsedQuery.funds = funds.map((x) => x.id);
  }
  if (investors && investors.length > 0) {
    parsedQuery.investors = investors.map((x) => x.objectId);
  }
  if (classifications && classifications.length > 0) {
    parsedQuery.classifications = classifications.map((x) => x.id);
  }
  if (read) {
    parsedQuery.readFlag = read;
  }
  if (period && period.startDate && period.endDate) {
    const startDate = new Date(period.startDate);
    const endDate = new Date(period.endDate);
    parsedQuery.fromPublishDate = formatDate(startDate, 'yyyy-MM-dd');
    parsedQuery.publishDate = formatDate(endDate, 'yyyy-MM-dd');
  }
  if (fromDate && effectiveChecked) {
    parsedQuery.fromDate = formatDate(fromDate, 'yyyy-MM-dd');
  }
  if (toDate && effectiveChecked) {
    parsedQuery.date = formatDate(toDate, 'yyyy-MM-dd');
  }
  if (fromDate && publishedChecked) {
    parsedQuery.fromPublishDate = formatDate(fromDate, 'yyyy-MM-dd');
  }
  if (asOfDate) {
    parsedQuery.publishDate = formatDate(asOfDate, 'yyyy-MM-dd');
  }
  if (toDate && publishedChecked) {
    parsedQuery.publishDate = formatDate(toDate, 'yyyy-MM-dd');
  }
  if (startResult) {
    parsedQuery.startResult = startResult;
  }
  if (maxResults) {
    parsedQuery.maxResults = maxResults;
  }

  parsedQuery.includeTotalCount = includeCount;

  if (sortField) {
    parsedQuery.sortField = sortField;
  }
  if (sortDir) {
    parsedQuery.sortDir = sortDir;
  }

  const stringified = queryString.stringify(parsedQuery);

  let endpoint = httpService.getEndpointURL(`/Documents/search/files?${stringified}`);
  return httpService.getTyped<any>(endpoint);
};

// PDF Calls
export async function returnPdf(endpoint): Promise<any> {
  const config = {
    headers: {
      'Content-type': 'application/pdf',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': '*',
    },
    responseType: 'arraybuffer',
  };

  return httpService
    .get(httpService.getEndpointURL(endpoint), config)
    .then((response) => {
      const data = response.data;
      const contentType = response.headers['content-type'];
      const uint8View = new Uint8Array(data);

      const blob = new Blob([data], { type: contentType });
      return { uint8: uint8View, blob: blob };
    });
}

export const getPins = (): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/Documents/assignments/pins`);
  return httpService.get(endpoint);
};

export const setPin = (assignId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/Documents/pin/assignment/${assignId}`);
  return httpService.put(endpoint);
};

export const removePin = (assignId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/Documents/unpin/assignment/${assignId}`);
  return httpService.put(endpoint);
};

export const getSlices = (sliceArray, documentName, funds, investors, classifications, read, fromDate, toDate, period, publishedChecked, effectiveChecked): Promise<any> => {
  let parsedQuery = queryString.parse(location.search);
  if (documentName) {
    parsedQuery.name = documentName;
  }
  if (funds && funds.length > 0) {
    parsedQuery.funds = funds.map((x) => x.id);
  }
  if (investors && investors.length > 0) {
    parsedQuery.investors = investors.map((x) => x.objectId);
  }
  if (classifications && classifications.length > 0) {
    parsedQuery.classifications = classifications.map((x) => x.id);
  }
  if (read) {
    parsedQuery.readFlag = read;
  }
  if (period && period.startDate && period.endDate) {
    const startDate = new Date(period.startDate);
    const endDate = new Date(period.endDate);
    parsedQuery.fromPublishDate = formatDate(startDate, 'yyyy-MM-dd');
    parsedQuery.publishDate = formatDate(endDate, 'yyyy-MM-dd');
  }
  if (fromDate && effectiveChecked) {
    parsedQuery.fromDate = formatDate(fromDate, 'yyyy-MM-dd');
  }
  if (toDate && effectiveChecked) {
    parsedQuery.date = formatDate(toDate, 'yyyy-MM-dd');
  }
  if (fromDate && publishedChecked) {
    parsedQuery.fromPublishDate = formatDate(fromDate, 'yyyy-MM-dd');
  }
  if (toDate && publishedChecked) {
    parsedQuery.publishDate = formatDate(toDate, 'yyyy-MM-dd');
  }
  if (sliceArray) {
    parsedQuery.sliceTypes = sliceArray;
  }

  const stringified = queryString.stringify(parsedQuery);

  let endpoint = httpService.getEndpointURL(`/Documents/slices?${stringified}`);
  return httpService.get(endpoint);
};

export const getDocsForSlice = (
  parentsArray,
  documentName,
  funds,
  investors,
  classifications,
  read,
  fromDate,
  toDate,
  startResult,
  maxResults,
  sortField,
  sortDir,
  publishedChecked,
  effectiveChecked,
  asOfDate,
  period,
  includeCount,
): Promise<any> => {
  let parsedQuery = queryString.parse(location.search);
  if (documentName) {
    parsedQuery.name = documentName;
  }
  if (funds && funds.length > 0) {
    parsedQuery.funds = funds.map((x) => x.id);
  }
  if (investors && investors.length > 0) {
    parsedQuery.investors = investors.map((x) => x.objectId);
  }
  if (classifications && classifications.length > 0) {
    parsedQuery.classifications = classifications.map((x) => x.id);
  }
  if (read) {
    parsedQuery.readFlag = read;
  }
  if (period && period.startDate && period.endDate) {
    const startDate = new Date(period.startDate);
    const endDate = new Date(period.endDate);
    parsedQuery.fromPublishDate = formatDate(startDate, 'yyyy-MM-dd');
    parsedQuery.publishDate = formatDate(endDate, 'yyyy-MM-dd');
  }
  if (fromDate && effectiveChecked) {
    parsedQuery.fromDate = formatDate(fromDate, 'yyyy-MM-dd');
  }
  if (toDate && effectiveChecked) {
    parsedQuery.date = formatDate(toDate, 'yyyy-MM-dd');
  }

  if (fromDate && publishedChecked) {
    parsedQuery.fromPublishDate = formatDate(fromDate, 'yyyy-MM-dd');
  }

  if (asOfDate) {
    parsedQuery.publishDate = formatDate(asOfDate, 'yyyy-MM-dd');
  }

  if (toDate && publishedChecked) {
    parsedQuery.publishDate = formatDate(toDate, 'yyyy-MM-dd');
  }

  if (startResult) {
    parsedQuery.startResult = startResult;
  }
  if (maxResults) {
    parsedQuery.maxResults = maxResults;
  }

  parsedQuery.includeTotalCount = includeCount;

  if (sortField) {
    parsedQuery.sortField = sortField;
  }
  if (sortDir) {
    parsedQuery.sortDir = sortDir;
  }

  const stringified = queryString.stringify(parsedQuery);

  let endpoint = httpService.getEndpointURL(`/Documents/search/files/slice?${stringified}`);
  return httpService.put(endpoint, parentsArray);
};

export const getFile = (id: number): Promise<any> => {
  return httpService.get(httpService.getEndpointURL(`/Documents/Download/${id}`), { responseType: 'arraybuffer' });
};

export const getPeriods = (frequency): Promise<any> => {
  let parsedQuery = queryString.parse(location.search);

  if (frequency) {
    parsedQuery.frequency = frequency;
  }

  const stringified = queryString.stringify(parsedQuery);

  let endpoint = httpService.getEndpointURL(`/Documents/periods?${stringified}`);
  return httpService.get(endpoint);
};

export const saveMultipleDocuments = (idArray): Promise<any> => {
  const config = {
    headers: {
      'Content-type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': '*',
    },
    responseType: 'arraybuffer',
  };
  let endpoint = httpService.getEndpointURL(`/Documents/download/batch`);
  return httpService.put(endpoint, idArray, config).then((response) => {
    const data = response.data;
    const contentType = response.headers['content-type'];
    const blob = new Blob([data], { type: contentType });
    const currentMoment = getDateAndTime();
    saveAs(blob, 'Documents_' + currentMoment);
  });
};
